import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { setSummaryCacheOwner, clearSavedHoldingsSummary } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set synchronously during render so child dashboards read the right owner on first paint.
  setSummaryCacheOwner(user?.id);

  const checkAuth = useCallback(async () => {
    // Skip network call if neither auth surface is present — avoids noisy 401s in DevTools.
    const hasJwt = !!localStorage.getItem("dp_token");
    const hasCookie = typeof document !== "undefined" && document.cookie.length > 0;
    if (!hasJwt && !hasCookie) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      if (localStorage.getItem("dp_token")) localStorage.removeItem("dp_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("dp_token", res.data.token);
    // Refetch via /auth/me so we get the enriched shape (is_admin, etc.)
    await checkAuth();
    return res.data.user;
  };

  const register = async (email, password, name, ref, acceptedVersions) => {
    const url = ref ? `/auth/register?ref=${encodeURIComponent(ref)}` : "/auth/register";
    const body = { email, password, name };
    if (acceptedVersions) body.accepted_versions = acceptedVersions;
    // Sticky affiliate attribution — set by any landing page seeing `?aff=slug`
    // and persisted in localStorage until the user actually signs up.
    const aff = localStorage.getItem("dp_affiliate_ref");
    if (aff) body.affiliate_slug = aff;
    const res = await api.post(url, body);
    localStorage.setItem("dp_token", res.data.token);
    if (aff) localStorage.removeItem("dp_affiliate_ref");
    await checkAuth();
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.debug("logout call failed (proceeding to clear local state):", err?.message || err); }
    localStorage.removeItem("dp_token");
    clearSavedHoldingsSummary();
    setUser(null);
  };

  const refreshUser = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
